import React, { useEffect, useMemo, useState } from "react";
import {
    Wallet,
    Check,
    X as XIcon,
    Rocket,
    TrendingUp,
    Gem,
    Globe,
    Settings2,
    ChevronUp,
    ChevronDown,
    Info,
    HelpCircle,
    Phone,
    PhoneOutgoing,
    MessageSquare,
    Clock,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import {
    PayPalCheckoutModal,
    snapshotShopForVerification,
} from "../components/PayPalCheckout";

const ICONS = { rocket: Rocket, "trending-up": TrendingUp, gem: Gem };

const ONE_TIME_ICONS = {
    NW_FIRST_HALF: { icon: Globe, color: "#3b82f6" },
    NW_SECOND_HALF: { icon: Globe, color: "#a855f7" },
    AUTO_SHOP_SETUP: { icon: Settings2, color: "#f59e0b" },
};

// Local-mock keys used by the (still-mocked) one-time payment recorder. We map
// the real packCode -> the local-mock key so existing /api/shop/one-time-payment
// keeps working until the real "purchase" API ships.
const PACK_CODE_TO_LOCAL_KEY = {
    NW_FIRST_HALF: "website_first_half",
    NW_SECOND_HALF: "website_second_half",
    AUTO_SHOP_SETUP: "setup_charges",
};

const PayPalLogo = () => (
    <span className="font-bold inline-flex items-center">
        <span style={{ color: "#003087" }}>Pay</span>
        <span style={{ color: "#009cde" }}>Pal</span>
    </span>
);

export default function Subscription({ shop, refresh }) {
    const [plans, setPlans] = useState([]);
    const [rechargePacks, setRechargePacks] = useState([]);
    const [oneTimePacks, setOneTimePacks] = useState([]);
    const [selectedPackCode, setSelectedPackCode] = useState(null);
    const [threshold, setThreshold] = useState(shop?.credits?.auto_recharge_threshold || 100);
    const [expandedPlan, setExpandedPlan] = useState(null);
    const [showCreditInfo, setShowCreditInfo] = useState(true);
    const [busy, setBusy] = useState(false);
    const [downgradeOpen, setDowngradeOpen] = useState(false);
    const [checkout, setCheckout] = useState(null); // {productId, productType, label, expectedPackCode?}

    useEffect(() => {
        // Master packs (subscription + recharge) – real API
        api.getMasterPacks()
            .then((m) => {
                // Subscription plans, sorted by price asc, with computed tier
                const subs = m.subscription
                    .map(api.adaptSubscriptionPack)
                    .sort((a, b) => a.price - b.price)
                    .map((p, i) => ({ ...p, tier: i + 1 }));
                setPlans(subs);
                // Recharge packs, sorted by price asc
                const recharges = m.recharge
                    .map(api.adaptRechargePack)
                    .sort((a, b) => a.amount - b.amount);
                setRechargePacks(recharges);
                if (recharges.length > 0) {
                    setSelectedPackCode((cur) => cur || recharges[0].packCode);
                }
            })
            .catch((err) =>
                toast.error(err?.message || "Failed to load subscription plans"),
            );

        // One-time payment packs (shop-scoped, returns isOneTimePayment flag)
        api.getOneTimePacks()
            .then(setOneTimePacks)
            .catch((err) => toast.error(err?.message || "Failed to load one-time packs"));
    }, []);

    const selectedPack =
        rechargePacks.find((p) => p.packCode === selectedPackCode) || rechargePacks[0];

    useEffect(() => {
        setThreshold(shop?.credits?.auto_recharge_threshold || 100);
    }, [shop]);

    const sub = shop?.subscription || {};
    const credits = shop?.credits || {};
    const totalBalance =
        (credits.subscription_credits || 0) + (credits.recharge_credits || 0);

    const currentTier = useMemo(() => {
        const cur = plans.find((p) => p.id === sub.plan_id);
        return cur?.tier || 0;
    }, [plans, sub]);

    const handleCheckoutSuccess = async () => {
        // Refresh shop + one-time packs (covers all 3 product types).
        try {
            await refresh?.();
            const packs = await api.getOneTimePacks();
            setOneTimePacks(packs);
            toast.success("Payment confirmed");
        } catch {
            /* ignore */
        }
    };

    const subscribe = (plan) => {
        if (!plan?.raw?._id) {
            toast.error("Plan id missing");
            return;
        }
        setCheckout({
            productId: plan.raw._id,
            productType: "SUBSCRIPTION",
            label: `${plan.name} plan`,
        });
    };

    const recharge = () => {
        if (!selectedPack?.raw?._id) return;
        setCheckout({
            productId: selectedPack.raw._id,
            productType: "RECHARGE",
            label: `$${selectedPack.amount} credits pack`,
        });
    };

    const cancelSub = async () => {
        if (!confirm("Cancel your subscription at end of cycle?")) return;
        try {
            await api.cancel();
            toast.success("Subscription will cancel at cycle end");
            refresh();
        } catch (e) {
            toast.error("Failed to cancel");
        }
    };

    const saveThreshold = async () => {
        try {
            await api.setAutoRecharge(
                Number(threshold),
                selectedPack?.amount || 250,
            );
            toast.success("Auto-recharge updated");
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to update threshold");
        }
    };

    const oneTime = (pack) => {
        if (!pack?._id) {
            toast.error("Pack id missing");
            return;
        }
        setCheckout({
            productId: pack._id,
            productType: "ONETIMEPAYMENT",
            label: pack.title,
            expectedPackCode: pack.packCode,
        });
    };

    if (!shop) return null;

    const upgradePlans = plans.filter((p) => p.tier > currentTier);
    const downgradePlans = plans.filter((p) => p.tier > 0 && p.tier < currentTier);
    const samePlan = plans.find((p) => p.tier === currentTier);

    return (
        <div className="flex flex-col gap-6 fade-in" data-testid="subscription-page">
            <div>
                <div className="ap-label mb-1" style={{ color: "var(--ap-blue-2)" }}>
                    Auto Shop Owner / Manager
                </div>
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
                    Subscription &amp; Payments
                </h1>
                <p className="text-sm mt-1" style={{ color: "var(--ap-text-dim)" }}>
                    Manage your plans and billing
                </p>
            </div>

            {/* Wallet card */}
            <div className="ap-card p-6 md:p-7" data-testid="wallet-card">
                <div className="flex items-start gap-5">
                    <div
                        className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
                        style={{
                            background: "rgba(245,158,11,0.12)",
                            color: "var(--ap-orange)",
                        }}
                    >
                        <Wallet size={26} />
                    </div>
                    <div className="flex-1">
                        <div className="ap-label">Total Balance</div>
                        <div className="text-4xl md:text-5xl font-extrabold tracking-tight mt-1">
                            {totalBalance.toLocaleString(undefined, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 1,
                            })}
                        </div>
                    </div>
                </div>

                <div
                    className="mt-6 ap-card-soft p-5 flex flex-col gap-4"
                    style={{ borderColor: "var(--ap-line-2)" }}
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-start gap-3">
                            <span
                                className="w-2.5 h-2.5 rounded-full mt-1.5"
                                style={{ background: "var(--ap-blue-2)" }}
                            />
                            <div>
                                <div className="font-semibold">Subscription Credits</div>
                                <div
                                    className="text-xs"
                                    style={{ color: "var(--ap-text-dim)" }}
                                >
                                    Resets each billing cycle
                                </div>
                            </div>
                        </div>
                        <div
                            className="text-2xl font-extrabold"
                            style={{ color: "var(--ap-blue-2)" }}
                        >
                            {(credits.subscription_credits || 0).toLocaleString(undefined, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 1,
                            })}
                        </div>
                    </div>
                    <div className="ap-divider" />
                    <div className="flex items-center justify-between">
                        <div className="flex items-start gap-3">
                            <span
                                className="w-2.5 h-2.5 rounded-full mt-1.5"
                                style={{ background: "var(--ap-green)" }}
                            />
                            <div>
                                <div className="font-semibold">Recharge Credits</div>
                                <div
                                    className="text-xs"
                                    style={{ color: "var(--ap-text-dim)" }}
                                >
                                    Never expire
                                </div>
                            </div>
                        </div>
                        <div
                            className="text-2xl font-extrabold"
                            style={{ color: "var(--ap-green)" }}
                        >
                            {(credits.recharge_credits || 0).toLocaleString()}
                        </div>
                    </div>
                </div>

                {/* Buy credits */}
                <div className="mt-7">
                    <div className="ap-label mb-3">Buy Credits</div>
                    <div
                        className="grid gap-3"
                        style={{
                            gridTemplateColumns: `repeat(${Math.max(rechargePacks.length, 1)}, minmax(0, 1fr))`,
                        }}
                    >
                        {rechargePacks.length === 0 && (
                            <div
                                className="ap-card-soft p-5 text-sm text-center"
                                style={{ color: "var(--ap-text-dim)" }}
                                data-testid="recharge-packs-loading"
                            >
                                Loading recharge packs…
                            </div>
                        )}
                        {rechargePacks.map((p) => {
                            const active = selectedPackCode === p.packCode;
                            return (
                                <button
                                    key={p.packCode}
                                    data-testid={`credit-pack-${p.packCode}`}
                                    onClick={() => setSelectedPackCode(p.packCode)}
                                    className="relative p-5 rounded-2xl transition-all text-center"
                                    style={{
                                        background: active
                                            ? "rgba(59,130,246,0.10)"
                                            : "var(--ap-card-2)",
                                        border: active
                                            ? "2px solid var(--ap-blue-2)"
                                            : "1px solid var(--ap-line-2)",
                                    }}
                                >
                                    {active && (
                                        <div
                                            className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center"
                                            style={{
                                                background: "var(--ap-blue-2)",
                                                color: "white",
                                            }}
                                        >
                                            <Check size={14} />
                                        </div>
                                    )}
                                    <div
                                        className="text-3xl font-extrabold"
                                        style={{
                                            color: active
                                                ? "var(--ap-blue-2)"
                                                : "var(--ap-text-dim)",
                                        }}
                                    >
                                        ${p.amount}
                                    </div>
                                    <div
                                        className="text-sm mt-1"
                                        style={{
                                            color: active
                                                ? "var(--ap-blue-2)"
                                                : "var(--ap-text-dim)",
                                        }}
                                    >
                                        {p.credits.toLocaleString()} Credits
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Auto-recharge */}
                <div className="mt-6">
                    <div className="ap-label mb-3">Auto-recharge Threshold</div>
                    <div className="relative">
                        <span
                            className="absolute left-4 top-1/2 -translate-y-1/2 font-semibold"
                            style={{ color: "var(--ap-text-dim)" }}
                        >
                            $
                        </span>
                        <input
                            data-testid="threshold-input"
                            type="number"
                            min={50}
                            value={threshold}
                            onChange={(e) => setThreshold(e.target.value)}
                            onBlur={saveThreshold}
                            className="ap-input pl-8 text-lg font-bold"
                        />
                    </div>
                    <div
                        className="text-xs mt-2 italic"
                        style={{ color: "var(--ap-text-dim)" }}
                    >
                        Min. $50 · Auto-recharges when balance drops below
                    </div>
                </div>

                {/* PayPal Buy button */}
                <button
                    data-testid="buy-credits-paypal"
                    disabled={busy || !selectedPack}
                    onClick={recharge}
                    className="ap-btn-paypal w-full mt-6 flex items-center justify-center gap-2 text-base"
                >
                    <span
                        className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-extrabold"
                        style={{ background: "#003087", color: "white" }}
                    >
                        P
                    </span>
                    Buy ${selectedPack?.amount ?? 0} Credits with <PayPalLogo />
                </button>
            </div>

            {/* Monthly Plans */}
            <div className="flex flex-col gap-3" data-testid="monthly-plans">
                <h2 className="text-2xl md:text-3xl font-bold">Monthly Plans</h2>
                <p className="text-sm" style={{ color: "var(--ap-text-dim)" }}>
                    Auto-renews monthly. Cancellations keep benefits until cycle end. Payments
                    once made are non-refundable.
                </p>

                {/* Current plan (highlighted) */}
                {samePlan && (
                    <PlanCard
                        plan={samePlan}
                        sub={sub}
                        expanded={expandedPlan === samePlan.id || !expandedPlan}
                        onToggle={() =>
                            setExpandedPlan(expandedPlan === samePlan.id ? null : samePlan.id)
                        }
                        isCurrent
                        cancelSub={cancelSub}
                        busy={busy}
                    />
                )}

                {/* Upgrade plans */}
                {upgradePlans.map((p) => (
                    <PlanCard
                        key={p.id}
                        plan={p}
                        expanded={expandedPlan === p.id}
                        onToggle={() => setExpandedPlan(expandedPlan === p.id ? null : p.id)}
                        actionLabel={`Upgrade to ${p.name}`}
                        onAction={() => subscribe(p.id, p.name, p.price)}
                        busy={busy}
                    />
                ))}

                {/* Downgrade plans collapsed */}
                {downgradePlans.length > 0 && (
                    <div
                        className="ap-card overflow-hidden"
                        data-testid="downgrade-section"
                    >
                        <button
                            onClick={() => setDowngradeOpen(!downgradeOpen)}
                            className="w-full p-5 flex items-center justify-between hover:bg-white/[0.02]"
                            data-testid="toggle-downgrade"
                        >
                            <div className="flex items-center gap-3">
                                <ChevronDown
                                    size={18}
                                    className="text-slate-400"
                                    style={{
                                        transform: downgradeOpen ? "rotate(180deg)" : "rotate(0deg)",
                                        transition: "transform 0.2s",
                                    }}
                                />
                                <span className="font-semibold">
                                    Downgrade Plan ({downgradePlans.length})
                                </span>
                            </div>
                        </button>
                        {downgradeOpen && (
                            <div className="px-5 pb-5 flex flex-col gap-3">
                                <div
                                    className="rounded-xl p-4 text-sm flex items-start gap-3"
                                    style={{
                                        background: "rgba(245,158,11,0.10)",
                                        color: "var(--ap-orange)",
                                    }}
                                >
                                    <Info size={18} className="shrink-0 mt-0.5" />
                                    Downgrading is only available after your current billing cycle
                                    ends.
                                </div>
                                {downgradePlans.map((p) => (
                                    <PlanCard
                                        key={p.id}
                                        plan={p}
                                        expanded={false}
                                        onToggle={() => {}}
                                        compact
                                        actionLabel={`Schedule downgrade to ${p.name}`}
                                        onAction={() => subscribe(p)}
                                        busy={busy}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* What can 1 credit do */}
            <div className="ap-card p-2" data-testid="credit-info-section">
                <button
                    onClick={() => setShowCreditInfo(!showCreditInfo)}
                    className="w-full p-4 flex items-center justify-between hover:bg-white/[0.02] rounded-xl"
                >
                    <div className="flex items-center gap-3">
                        <div
                            className="w-9 h-9 rounded-full flex items-center justify-center"
                            style={{
                                background: "var(--ap-blue-soft)",
                                color: "var(--ap-blue-2)",
                            }}
                        >
                            <HelpCircle size={18} />
                        </div>
                        <span className="font-semibold">What can 1 credit do?</span>
                    </div>
                    {showCreditInfo ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>
                {showCreditInfo && (
                    <div className="p-5 pt-0 flex flex-col">
                        <CreditRow icon={Phone} label="1 Inbound phone call" sub="Max 4 min. Billed per minute." value="3 Credits" />
                        <CreditRow icon={PhoneOutgoing} label="1 Outbound follow-up call" value="1.5 Credits" />
                        <CreditRow icon={Globe} label="1 Website conversation" value="1 Credit" />
                        <CreditRow icon={MessageSquare} label="1 SMS conversation" sub="Up to 10 messages per conversation" value="1.5 Credits" />
                        <div
                            className="mt-3 text-xs flex items-start gap-2"
                            style={{ color: "var(--ap-text-dim)" }}
                        >
                            <Info size={14} className="shrink-0 mt-0.5" />
                            Calls exceeding 7 min 30 sec are transferred to a human or scheduled
                            for callback.
                        </div>
                        <div
                            className="text-xs flex items-start gap-2 mt-1"
                            style={{ color: "var(--ap-text-dim)" }}
                        >
                            <MessageSquare size={14} className="shrink-0 mt-0.5" />1 conversation = 1
                            booked appointment, estimate, or enquiry with a few rounds of
                            customer-agent exchange.
                        </div>
                    </div>
                )}
            </div>

            {/* One-time payments */}
            <div className="flex flex-col gap-3" data-testid="one-time-payments">
                <h2 className="text-2xl md:text-3xl font-bold">One-time Payments</h2>
                <p className="text-sm" style={{ color: "var(--ap-text-dim)" }}>
                    Pay project fees and setup charges securely with PayPal.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {oneTimePacks.length === 0 && (
                        <div
                            className="md:col-span-3 ap-card-soft p-6 text-center text-sm"
                            style={{ color: "var(--ap-text-dim)" }}
                            data-testid="one-time-empty"
                        >
                            Loading available one-time packs…
                        </div>
                    )}
                    {oneTimePacks.map((p) => {
                        const meta = ONE_TIME_ICONS[p.packCode] || {
                            icon: Settings2,
                            color: "#3b82f6",
                        };
                        const Icon = meta.icon;
                        const paid = !!p.isOneTimePayment;
                        return (
                            <div
                                key={p._id}
                                className="ap-card p-5 flex flex-col gap-4 relative"
                                data-testid={`one-time-${p.packCode}`}
                                style={
                                    paid
                                        ? { border: "1px solid rgba(34,197,94,0.45)" }
                                        : undefined
                                }
                            >
                                {paid && (
                                    <div
                                        className="absolute top-3 right-3 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider flex items-center gap-1"
                                        style={{
                                            background: "rgba(34,197,94,0.15)",
                                            color: "#4ade80",
                                        }}
                                        data-testid={`paid-badge-${p.packCode}`}
                                    >
                                        <Check size={11} strokeWidth={3} /> Paid
                                    </div>
                                )}
                                <div
                                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                                    style={{
                                        background: `${meta.color}22`,
                                        color: meta.color,
                                    }}
                                >
                                    <Icon size={22} />
                                </div>
                                <div className="flex-1">
                                    <div className="font-semibold">{p.title}</div>
                                    {p.tags?.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                                            {p.tags.slice(0, 3).map((t) => (
                                                <span
                                                    key={t}
                                                    className="px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider"
                                                    style={{
                                                        background: "var(--ap-blue-soft)",
                                                        color: "var(--ap-blue-2)",
                                                    }}
                                                >
                                                    {t}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    <div
                                        className="text-3xl font-extrabold mt-3"
                                        style={{ color: "white" }}
                                    >
                                        ${p.amount}
                                        <span
                                            className="text-sm font-medium ml-1.5"
                                            style={{ color: "var(--ap-text-dim)" }}
                                        >
                                            {p.currency}
                                        </span>
                                    </div>
                                </div>
                                <button
                                    data-testid={`pay-${p.packCode}`}
                                    disabled={paid}
                                    onClick={() => !paid && oneTime(p)}
                                    type="button"
                                    className={`w-full flex items-center justify-center gap-2 text-sm ${
                                        paid ? "ap-btn-ghost cursor-not-allowed" : "ap-btn-paypal"
                                    }`}
                                >
                                    {paid ? (
                                        <>
                                            <Check size={14} /> Already paid
                                        </>
                                    ) : (
                                        <>
                                            <span
                                                className="w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-extrabold"
                                                style={{
                                                    background: "#003087",
                                                    color: "white",
                                                }}
                                            >
                                                P
                                            </span>
                                            Pay ${p.amount} with <PayPalLogo />
                                        </>
                                    )}
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>

            <PayPalCheckoutModal
                open={!!checkout}
                productId={checkout?.productId}
                productType={checkout?.productType}
                productLabel={checkout?.label}
                expectedPackCode={checkout?.expectedPackCode}
                baseline={snapshotShopForVerification(shop)}
                onClose={() => setCheckout(null)}
                onSuccess={() => {
                    setCheckout(null);
                    handleCheckoutSuccess();
                }}
            />
        </div>
    );
}

function CreditRow({ icon: Icon, label, sub, value }) {
    return (
        <div
            className="py-3 flex items-center gap-3 border-t"
            style={{ borderColor: "var(--ap-line)" }}
        >
            <Icon size={18} style={{ color: "var(--ap-blue-2)" }} />
            <div className="flex-1">
                <div className="text-sm font-medium text-white">{label}</div>
                {sub && (
                    <div className="text-xs" style={{ color: "var(--ap-text-dim)" }}>
                        {sub}
                    </div>
                )}
            </div>
            <span
                className="px-3 py-1 rounded-full text-xs font-semibold"
                style={{ background: "var(--ap-blue-soft)", color: "var(--ap-blue-2)" }}
            >
                {value}
            </span>
        </div>
    );
}

function PlanCard({
    plan,
    sub,
    expanded,
    onToggle,
    isCurrent,
    actionLabel,
    onAction,
    cancelSub,
    busy,
    compact,
}) {
    const Icon = ICONS[plan.icon] || Rocket;
    const cancelled = isCurrent && sub?.cancels_at_cycle_end;
    const cycleEnd = sub?.cycle_end ? new Date(sub.cycle_end).toLocaleDateString() : null;

    return (
        <div
            className={`ap-card overflow-hidden transition-all ${expanded ? "scale-in" : ""}`}
            style={{
                border: isCurrent
                    ? `2px solid ${cancelled ? "var(--ap-orange)" : "var(--ap-green)"}`
                    : "1px solid var(--ap-line)",
            }}
            data-testid={`plan-${plan.id}`}
        >
            {isCurrent && cancelled && (
                <div
                    className="px-5 py-2.5 text-xs font-bold uppercase tracking-wide flex items-center gap-2 justify-end"
                    style={{
                        background: "rgba(245,158,11,0.10)",
                        color: "var(--ap-orange)",
                    }}
                >
                    <Clock size={14} /> Cancels at cycle end
                </div>
            )}

            <button
                onClick={onToggle}
                className="w-full p-5 flex items-center justify-between gap-4 hover:bg-white/[0.02]"
            >
                <div className="flex items-center gap-4">
                    <div
                        className="w-12 h-12 rounded-2xl flex items-center justify-center"
                        style={{ background: `${plan.icon_color}22`, color: plan.icon_color }}
                    >
                        <Icon size={22} />
                    </div>
                    <div className="text-left">
                        <div className="text-xl font-bold">{plan.name}</div>
                        <div className="text-xs" style={{ color: "var(--ap-text-dim)" }}>
                            {plan.description}
                        </div>
                    </div>
                </div>
                <div className="text-right flex items-center gap-3">
                    <div>
                        <div className="text-2xl font-extrabold">${plan.price}</div>
                        <div className="text-xs" style={{ color: "var(--ap-text-dim)" }}>
                            /{plan.period}
                        </div>
                    </div>
                    {!compact &&
                        (expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />)}
                </div>
            </button>

            {expanded && !compact && (
                <div className="px-5 pb-5 flex flex-col gap-3">
                    {plan.features.map((f) => (
                        <div key={f} className="flex items-center gap-3">
                            <span
                                className="w-5 h-5 rounded-full flex items-center justify-center"
                                style={{ background: "rgba(34,197,94,0.18)" }}
                            >
                                <Check size={12} className="text-green-400" strokeWidth={3} />
                            </span>
                            <span className="text-sm">{f}</span>
                        </div>
                    ))}
                    {(plan.excluded_features || []).map((f) => (
                        <div
                            key={f}
                            className="flex items-center gap-3"
                            style={{ color: "var(--ap-text-muted)" }}
                        >
                            <span
                                className="w-5 h-5 rounded-full flex items-center justify-center"
                                style={{ background: "rgba(148,163,184,0.18)" }}
                            >
                                <XIcon size={12} strokeWidth={3} />
                            </span>
                            <span className="text-sm line-through">{f}</span>
                        </div>
                    ))}

                    {isCurrent && cycleEnd && (
                        <div
                            className="ap-card-soft p-4 mt-2 flex items-center justify-between"
                            data-testid={`current-plan-info-${plan.id}`}
                        >
                            <div className="flex items-center gap-2 text-sm">
                                <Clock
                                    size={16}
                                    style={{ color: "var(--ap-orange)" }}
                                />
                                Benefits until
                            </div>
                            <div
                                className="font-semibold"
                                style={{ color: "var(--ap-orange)" }}
                            >
                                {cycleEnd}
                            </div>
                        </div>
                    )}
                    {isCurrent && cancelled && (
                        <div
                            className="text-sm italic"
                            style={{ color: "var(--ap-orange)" }}
                        >
                            Cancelled — you can still upgrade to a higher plan before the cycle
                            ends.
                        </div>
                    )}

                    <div className="flex flex-col md:flex-row gap-3 mt-2">
                        {actionLabel && onAction && (
                            <button
                                data-testid={`plan-action-${plan.id}`}
                                disabled={busy}
                                onClick={onAction}
                                className="ap-btn-paypal flex-1 flex items-center justify-center gap-2 text-sm"
                            >
                                <span
                                    className="w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-extrabold"
                                    style={{ background: "#003087", color: "white" }}
                                >
                                    P
                                </span>
                                {actionLabel} · ${plan.price} with <PayPalLogo />
                            </button>
                        )}
                        {isCurrent && !cancelled && (
                            <button
                                data-testid={`cancel-plan-${plan.id}`}
                                onClick={cancelSub}
                                className="ap-btn-danger flex-1"
                            >
                                Cancel subscription
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
